// API client for the Assessment content hierarchy:
// Course -> Subject -> Chapter -> Topic -> Sub-Topic.
//
// Backend contract (sia-backend, /api/assessment/admin/*):
//   - every response is { data, isError, statusCode, errorMessage }
//   - list endpoints for subjects/chapters/topics/subtopics return a page in
//     `data`: { content, page, size, totalElements, totalPages, hasNext }
//     (GET /courses returns a plain array)
//   - ids are integers; every parent/child link is many-to-many, so rows carry
//     `xxxIds` / `xxxNames` arrays instead of a single parent id
//   - DELETE on subject/chapter/topic/subtopic is two-step: without
//     `confirm=true` it only returns a preview, with it the delete happens
//
// Rows are normalised here (status string, `title` alias for `name`, a
// convenience single-parent id) so screens and the cascading pickers keep
// working with the shapes they already render.
import api from "../axiosInstance";

const BASE = "/api/assessment/admin";
const FETCH_ALL_PAGE_SIZE = 200;

export const apiErrorMessage = (err, fallback) =>
  err?.response?.data?.errorMessage ||
  err?.response?.data?.message ||
  err?.message ||
  fallback;

const statusOf = (row) => (row.active ? "ACTIVE" : "INACTIVE");

export const normalizeCourse = (c) => ({
  ...c,
  status: statusOf(c),
  subjectCount: c.subjectCount ?? 0,
  chapterCount: c.chapterCount ?? 0,
  topicCount: c.topicCount ?? 0,
  subTopicCount: c.subTopicCount ?? 0,
  videoCount: c.videoCount ?? 0,
});

export const normalizeSubject = (s) => ({
  ...s,
  status: statusOf(s),
  courseIds: s.courseIds ?? [],
  courseNames: s.courseNames ?? [],
  chapterCount: s.chapterCount ?? 0,
});

export const normalizeChapter = (c) => ({
  ...c,
  title: c.name,
  status: statusOf(c),
  subjectIds: c.subjectIds ?? [],
  subjectNames: c.subjectNames ?? [],
  subjectId: c.subjectIds?.[0],
  topicCount: c.topicCount ?? 0,
  questionCount: c.questionCount ?? 0,
});

export const normalizeTopic = (t) => ({
  ...t,
  title: t.name,
  status: statusOf(t),
  chapterIds: t.chapterIds ?? [],
  chapterNames: t.chapterNames ?? [],
  subjectIds: t.subjectIds ?? [],
  subjectNames: t.subjectNames ?? [],
  chapterId: t.chapterIds?.[0],
  subTopicCount: t.subTopicCount ?? 0,
  questionCount: t.questionCount ?? 0,
});

export const normalizeSubTopic = (st) => ({
  ...st,
  title: st.name,
  status: statusOf(st),
  topicIds: st.topicIds ?? [],
  topicNames: st.topicNames ?? [],
  chapterIds: st.chapterIds ?? [],
  chapterNames: st.chapterNames ?? [],
  subjectIds: st.subjectIds ?? [],
  subjectNames: st.subjectNames ?? [],
  topicId: st.topicIds?.[0],
  questionCount: st.questionCount ?? 0,
});

// Drop undefined/empty-string params so they aren't sent as `?search=`.
const cleanParams = (params = {}) =>
  Object.fromEntries(
    Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== ""),
  );

const unwrap = (res) => res.data?.data;

// One page of a paginated list, normalised. Returns the page envelope with
// `content` mapped through `normalize`.
async function listPage(path, normalize, { page = 0, size = 20, ...filters } = {}) {
  const res = await api.get(`${BASE}/${path}`, {
    params: cleanParams({ ...filters, page, size }),
  });
  const data = unwrap(res) || {};
  return { ...data, content: (data.content || []).map(normalize) };
}

// Every row of a paginated list (for cascading pickers / lookups that need the
// whole collection rather than a single table page).
async function listAll(path, normalize, filters = {}) {
  const rows = [];
  for (let page = 0; ; page += 1) {
    const data = await listPage(path, normalize, { ...filters, page, size: FETCH_ALL_PAGE_SIZE });
    rows.push(...data.content);
    if (!data.hasNext) break;
  }
  return rows;
}

// --- Courses ---------------------------------------------------------------
// Courses are a plain (unpaginated) array. Payload fields:
// name, description, type, displayOrder, active.
// (Which student courses see a category, and its readiness target, are set per category - see
// api/assessmentCategories.js.)
export const listCourses = async ({ includeInactive = true } = {}) => {
  const res = await api.get(`${BASE}/courses`, { params: { includeInactive } });
  return (unwrap(res) || []).map(normalizeCourse);
};
// Regular courses ({ id, name }) an assessment course can be mapped to.
export const listMappableCourses = async () =>
  unwrap(await api.get(`${BASE}/courses/mappable-courses`)) || [];
export const createCourse = (payload) => api.post(`${BASE}/courses`, payload).then(unwrap);
export const updateCourse = (id, payload) => api.put(`${BASE}/courses/${id}`, payload).then(unwrap);
// Soft delete: the backend just marks the course inactive.
export const deleteCourse = (id) => api.delete(`${BASE}/courses/${id}`).then(unwrap);

// --- Subjects --------------------------------------------------------------
// filters: courseId, isActive, search. Payload: name, description, courseIds[],
// displayOrder, active.
export const listSubjects = (opts) => listPage("subjects", normalizeSubject, opts);
export const listAllSubjects = (filters) => listAll("subjects", normalizeSubject, filters);
export const createSubject = (payload) => api.post(`${BASE}/subjects`, payload).then(unwrap);
export const updateSubject = (id, payload) => api.put(`${BASE}/subjects/${id}`, payload).then(unwrap);

// --- Chapters --------------------------------------------------------------
// filters: subjectId, isActive, search. Payload: name, description, subjectIds[],
// displayOrder, active.
export const listChapters = (opts) => listPage("chapters", normalizeChapter, opts);
export const listAllChapters = (filters) => listAll("chapters", normalizeChapter, filters);
export const createChapter = (payload) => api.post(`${BASE}/chapters`, payload).then(unwrap);
export const updateChapter = (id, payload) => api.put(`${BASE}/chapters/${id}`, payload).then(unwrap);
// The uploaded synopsis of a chapter: { subject, chapter, weightage, mindmap, topics:[{ heading,
// periods:[{ subtopics:[{ heading, content, solvedQuestions:[{ question, options, correctAnswer,
// explanation }] }] }] }] }. Every text field is an HTML string.
export const getChapterSynopsis = (id) => api.get(`${BASE}/chapters/${id}/synopsis`).then(unwrap);

// --- Topics ----------------------------------------------------------------
// filters: subjectId, chapterId, isActive, search. Payload: name, description,
// chapterIds[], subjectIds[] (validation only), displayOrder, active.
export const listTopics = (opts) => listPage("topics", normalizeTopic, opts);
export const listAllTopics = (filters) => listAll("topics", normalizeTopic, filters);
export const createTopic = (payload) => api.post(`${BASE}/topics`, payload).then(unwrap);
export const updateTopic = (id, payload) => api.put(`${BASE}/topics/${id}`, payload).then(unwrap);

// --- Sub-Topics ------------------------------------------------------------
// filters: subjectId, chapterId, topicId, isActive, search. Payload: name,
// description, topicIds[], chapterIds[] + subjectIds[] (validation only),
// displayOrder, active.
export const listSubTopics = (opts) => listPage("subtopics", normalizeSubTopic, opts);
export const listAllSubTopics = (filters) => listAll("subtopics", normalizeSubTopic, filters);
export const createSubTopic = (payload) => api.post(`${BASE}/subtopics`, payload).then(unwrap);
export const updateSubTopic = (id, payload) => api.put(`${BASE}/subtopics/${id}`, payload).then(unwrap);

// --- Two-step deletes ------------------------------------------------------
const DELETE_PATHS = {
  subject: "subjects",
  chapter: "chapters",
  topic: "topics",
  subtopic: "subtopics",
};

// Step 1: ask the backend what deleting would remove (nothing is deleted).
// Resolves with the preview ({ id, name, message, ...counts }). Rejects with
// the backend error when a guard blocks the delete (e.g. still referenced by
// questions or tests) so the UI can surface the reason before confirming.
export const previewDelete = (kind, id) =>
  api.delete(`${BASE}/${DELETE_PATHS[kind]}/${id}`, { params: { confirm: false } }).then(unwrap);

// Step 2: actually delete. Resolves with the confirmation payload.
export const confirmDelete = (kind, id) =>
  api.delete(`${BASE}/${DELETE_PATHS[kind]}/${id}`, { params: { confirm: true } }).then(unwrap);

// --- Bulk import -----------------------------------------------------------
// Uploads a .xlsx/.csv of Subject, Chapter, Topic, SubTopic rows. courseIds are the courses new
// subjects are linked to. dryRun=true validates only (nothing is saved). HTTP 200 even when rows
// are invalid; resolves with { dryRun, mapped: { subjects, chapters, topics, subTopics },
// skippedExisting, rows: [{ row, status: MAP|EXISTS|ERROR, message }],
// errors: [{ row, errorMessage }] }. When errors is non-empty nothing was saved.
export const bulkImportHierarchy = (file, courseIds = [], dryRun = false) => {
  const formData = new FormData();
  formData.append("file", file);
  courseIds.forEach((id) => formData.append("courseIds", id));
  formData.append("dryRun", dryRun);
  return api.post(`${BASE}/hierarchy/bulk-upload`, formData).then(unwrap);
};
